import { renderDiagnostic } from '@jsonfmt/core';

import type { Command, CommandContext, CommandResult, DocumentSnapshot, Notice } from '../types';
import { EXAMPLE_LABEL, type ExampleId, loadExample } from './examples';

/**
 * THE FOUR DOCUMENT COMMANDS — copy, paste, clear, openExamples (ADR-0014,
 * docs/ARCHITECTURE.md §4.9, recon §1.2).
 *
 * Every one of them returns a `CommandResult` and performs nothing: `app/runCommand.ts` is
 * the single place that writes to the store and shows at most one toast. `notice` being ONE
 * optional field is what makes "one action, one toast" a property of the type — the legacy
 * emitted two and ordered them with `setTimeout(…, 100)`, which is a race wearing a
 * function call.
 *
 * ── THE FOUR CONTRACT POINTS INHERITED FROM THE LEGACY BUG LIST ─────────────────────────
 *
 * 1. COPY COPIES WHAT IS ON SCREEN, byte for byte. Never the formatted text, never a
 *    re-serialisation. If the buffer is not valid JSON the copy still happens and the toast
 *    says so in ONE translated sentence (`toast.copy.successInvalid`) — the legacy built
 *    that sentence by concatenating its copy-success string with a literal
 *    ` (Note: Invalid JSON)`, an English suffix no locale could ever reach.
 * 2. PASTE INSERTS EVEN WHEN THE TEXT DOES NOT PARSE, and says what is wrong with it using
 *    the REAL diagnostic. The user pasted it in order to fix it; refusing to show it is the
 *    one thing that helps nobody.
 * 3. CLEAR IS UNDOABLE AND SAYS SO. It is one `document` replacement, therefore one undo
 *    step, and the toast names the key that brings the buffer back. No confirm dialog: undo
 *    is the safety net (recon §1.2).
 * 4. A CAPABILITY THAT IS NOT THERE IS NOT RENDERED. `isAvailable` reads the clipboard's
 *    feature-detection flags, so Firefox — which never grants `readText()` to a page — gets
 *    no Paste button instead of a button that always fails.
 */

// ── keycaps ──────────────────────────────────────────────────────────────────────────────

/**
 * The keycaps a toast prints inside its sentence.
 *
 * DESIGN §7.4 renders `⌘` on macOS and `Ctrl` elsewhere, and decides which once at load —
 * but that decision reads `navigator`, and this file is compiled by `tsconfig.core.json`
 * with `lib: ["ES2022"]` and `types: []`, where `navigator` does not exist (gate 1). So the
 * command states BOTH spellings rather than guessing one or interpolating a raw `{{shortcut}}`
 * into the user's face. If `app/runCommand.ts` later grows the platform keycap renderer the
 * toolbar already needs for `shortcutHint`, it can substitute a single keycap here; until
 * then this is true on every platform, which a guess would not be.
 */
export const UNDO_KEYCAP = 'Ctrl+Z / ⌘Z';
export const PASTE_KEYCAP = 'Ctrl+V / ⌘V';

/** One toast id per command, so hammering a control replaces rather than stacks (recon §1.2). */
const COPY_TOAST = 'document.copy';
const PASTE_TOAST = 'document.paste';
const CLEAR_TOAST = 'document.clear';

/** The undo-step labels. One spelling each, shared with the tests and `runCommand`. */
export const CLEAR_LABEL = 'document.clear';
export const PASTE_LABEL = 'document.paste';

const EMPTY_NOTICE = (id: string): Notice => ({ tone: 'error', key: 'toast.error.empty', id });

function isBlank(text: string): boolean {
  return text.trim().length === 0;
}

/**
 * Is this buffer valid JSON?
 *
 * The snapshot's own report is used when it describes THIS text (`reportVersion` equal to
 * `version` — ADR-0009 makes staleness a comparison rather than a race). Otherwise the
 * question is put to the analyzer, which is also the only path that is correct for a
 * document that has never been linted, e.g. one typed while the debounce was still running.
 * Above 256 KiB that call goes to a Worker and this file does not want to know (ADR-0010).
 */
async function isValid(ctx: CommandContext, doc: DocumentSnapshot): Promise<boolean> {
  if (doc.report !== null && doc.reportVersion === doc.version) return doc.report.ok;
  const report = await ctx.services.analyzer.lint(doc.text);
  return report.ok;
}

// ── copy ────────────────────────────────────────────────────────────────────────────────

export const copyCommand: Command = {
  id: 'document.copy',
  labelKey: 'action.copy',
  icon: 'copy',
  group: 'clipboard',
  variant: 'tinted',
  // Peri, the neutral family: copying changes nothing, so it claims no meaning.
  hue: 'peri',

  // No `shortcut`. `Mod-C` must stay the browser's own selection-copy: binding it here would
  // make the toolbar and the keymap agree on stealing a key every developer already owns
  // (recon §1.7). The control is reachable by Tab and by pointer, which is what it is for.

  isAvailable: (ctx) => ctx.services.clipboard.canWrite,
  isEnabled: (doc) => !isBlank(doc.text),

  async run(ctx, doc): Promise<CommandResult> {
    if (isBlank(doc.text)) return { notice: EMPTY_NOTICE(COPY_TOAST) };

    try {
      // VERBATIM. Not `format(doc.text)`, not `JSON.stringify(JSON.parse(...))`: what the
      // user sees selected is what lands on their clipboard.
      await ctx.services.clipboard.write(doc.text);
    } catch {
      // `ClipboardService` never rejects with a raw `DOMException` and no rejection carries
      // the user's text, so there is nothing here worth inspecting — only a sentence to
      // show. The distinction that matters (no clipboard at all) was already made by
      // `isAvailable`, which did not render this control.
      return { notice: { tone: 'error', key: 'toast.error.clipboardWrite', id: COPY_TOAST } };
    }

    // The bytes are already on the clipboard; only the WORDING depends on the analysis. A
    // lint that fails after a successful write must not turn a copy into an error.
    let valid: boolean;
    try {
      valid = await isValid(ctx, doc);
    } catch {
      valid = true;
    }

    return {
      notice: valid
        ? { tone: 'success', key: 'toast.copy.success', id: COPY_TOAST }
        : // ONE sentence, whole, in the locale file. Never success + an English suffix.
          { tone: 'warning', key: 'toast.copy.successInvalid', id: COPY_TOAST },
    };
  },
};

// ── paste ───────────────────────────────────────────────────────────────────────────────

export const pasteCommand: Command = {
  id: 'document.paste',
  labelKey: 'action.paste',
  icon: 'paste',
  group: 'clipboard',
  variant: 'tinted',
  hue: 'peri',

  // No `shortcut` either: `Mod-V` belongs to the editor, where CodeMirror's own paste path
  // runs `features/editor/pasteHandler.ts` synchronously inside the transaction.

  isAvailable: (ctx) => ctx.services.clipboard.canRead,

  async run(ctx): Promise<CommandResult> {
    let text: string;
    try {
      text = await ctx.services.clipboard.read();
    } catch {
      return {
        notice: {
          tone: 'error',
          key: 'toast.error.clipboardRead',
          params: { shortcut: PASTE_KEYCAP },
          id: PASTE_TOAST,
        },
      };
    }

    if (isBlank(text)) return { notice: EMPTY_NOTICE(PASTE_TOAST) };

    const report = await ctx.services.analyzer.lint(text);

    // CONTRACT POINT 2. The text goes in either way — one replacement, one undo step — and
    // an unparseable paste is reported with the first real diagnostic rather than a bare
    // "Invalid JSON". `reveal` is safe because the offsets describe exactly the text being
    // inserted.
    const document = { text, label: PASTE_LABEL };
    const first = report.diagnostics.find((d) => d.severity === 'error');

    if (report.ok || first === undefined) {
      return { document, notice: { tone: 'success', key: 'toast.paste.success', id: PASTE_TOAST } };
    }

    return {
      document,
      reveal: first.span,
      notice: {
        tone: 'error',
        key: 'toast.error.invalid',
        // The engine's catalog, so the sentence is translated by whatever locale is loaded
        // and cannot drift from the check that produced it (ADR-0008, ADR-0018).
        params: { message: renderDiagnostic(first, ctx.catalog).message },
        id: PASTE_TOAST,
      },
    };
  },
};

// ── clear ───────────────────────────────────────────────────────────────────────────────

export const clearCommand: Command = {
  id: 'document.clear',
  labelKey: 'action.clear',
  icon: 'clear',
  group: 'document',
  variant: 'tinted',
  // Coral: "you will lose bytes" — the single danger-hued control (DESIGN §6.9, ADR-0101 §1).
  // Stated here rather than promoted by id in App.tsx.
  hue: 'coral',
  // DESIGN §7.4: `⌘⌫`. Recon proposed `Mod-Shift-Backspace`; the approved design sheet wins,
  // and `Mod-Backspace` is `deleteGroupBackward` in CodeMirror's default keymap — ours has
  // the higher precedence, and the toolbar prints the same string it fires.
  shortcut: 'Mod-Backspace',

  isEnabled: (doc) => doc.text.length > 0,

  run(_ctx, doc): CommandResult {
    // "Skipped entirely if already empty" (recon §1.2): no version bump, and no toast that
    // claims to have done something. An empty buffer cleared is not an event.
    if (doc.text.length === 0) return {};

    return {
      document: { text: '', label: CLEAR_LABEL },
      notice: {
        tone: 'info',
        key: 'toast.clear.success',
        params: { shortcut: UNDO_KEYCAP },
        id: CLEAR_TOAST,
      },
    };
  },
};

// ── openExamples ────────────────────────────────────────────────────────────────────────

export const openExamplesCommand: Command = {
  id: 'document.open-examples',
  labelKey: 'action.loadExample',
  // "Example" below 960px. The longest of the seven toolbar words, on the control that lands
  // at the end of the phone dock's second row — four characters are the difference between a
  // flush row at 360px and a fourth one (ADR-0101 §7.1, §7.6).
  compactLabelKey: 'action.loadExampleShort',
  icon: 'examples',
  group: 'document',
  variant: 'tinted',
  // Violet: "discover", the hue of the examples dialog it opens (ADR-0101 §1).
  hue: 'violet',
  // After the toolbar's flexible gap (DESIGN §6.2). Placement is registry knowledge.
  align: 'end',
  shortcut: 'Mod-e',

  run(ctx): CommandResult {
    // The acknowledged dent in "a command performs nothing", and the reason `UiPort` exists
    // with exactly four methods (ADR-0014): opening a modal is imperative, lives in the
    // React tree, and cannot be expressed as data without inventing a dialog state machine
    // that v1 does not need. `closeDialogs` is the opposite direction and IS data.
    ctx.ui.openDialog('examples');
    return {};
  },
};

// ── loading one example ─────────────────────────────────────────────────────────────────

/**
 * The command a row of `ExamplesDialog` runs. Built per id rather than reading a selection
 * out of a module-level variable, so nothing is shared between two dialogs, or between two
 * documents once a Diff View exists.
 *
 * No toast: the dialog's subtitle has already said that loading replaces the buffer and that
 * undo puts it back, and a toast that repeats the sentence the user just read is noise. The
 * visible result is the document itself.
 */
export function loadExampleCommand(id: ExampleId): Command {
  return {
    id: `document.load-example.${id}`,
    labelKey: 'action.loadExample',
    icon: 'examples',
    group: 'document',

    async run(ctx): Promise<CommandResult> {
      const text = await loadExample(id);
      // One `document` field is one `store.replace`, which is one transaction, which is one
      // undo step — the promise the dialog's subtitle makes.
      ctx.ui.focusEditor();
      return { document: { text, label: EXAMPLE_LABEL }, closeDialogs: true };
    },
  };
}

/** What `features/registry.ts` spreads. Order is toolbar order (DESIGN §6.2). */
export const documentCommands: readonly Command[] = [
  copyCommand,
  pasteCommand,
  clearCommand,
  openExamplesCommand,
];
